import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type {
  CrewWeek,
  DayLine,
  JobSummary,
  PayPeriod,
  PerDiemConflict,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { cutoffLine } from '@/lib/cutoff'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** ISO date of day `i` (0 = Sunday) in the week ending `weekEnding`. */
function dayIso(weekEnding: string, i: number): string {
  const [y, m, d] = weekEnding.split('-').map(Number)
  const dt = new Date(y, m - 1, d - 6 + i)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`
}

/** "2026-08-08" -> "Sat 8 Aug". Parsed as parts, not via Date(), because
 *  `new Date('2026-08-08')` is UTC midnight and renders as the 7th west of
 *  Greenwich — which is exactly where these crews are. */
function dayLabel(iso: string, withYear = false): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dt.getDay()]
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]
  return `${wd} ${d} ${mon}${withYear ? ` ${y}` : ''}`
}

const ROLE_CLASS: Record<string, string> = {
  Super: 'role-super',
  QC: 'role-qc',
  Technician: 'role-tech',
}

/**
 * A crew's whole week, across every job.
 *
 * The existing week view is keyed by JOB, which answers "what went into this
 * job" but not "what is my crew's week". A crew routinely splits across two or
 * three jobs, so a per-job view means opening three screens and adding them up
 * — and it never shows the person who was moved mid-week.
 *
 * Laid out as the paper sheet is: people down, Sunday to Saturday across.
 */
export function CrewWeekPage() {
  const { user } = useAuth()
  const isSupervisor = user?.role === 'Supervisor'

  const [periods, setPeriods] = useState<PayPeriod[]>([])
  const [weekEnding, setWeekEnding] = useState('')
  const [crews, setCrews] = useState<CrewWeek[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* --- Per diem editing ---------------------------------------------------
   *
   * The grid is the natural place to record per diem for days nobody worked:
   * it already shows the whole week for the whole crew, so the weekend the
   * crew stayed over is two empty boxes sitting right where the answer goes.
   *
   * One crew at a time. Per diem is costed to a job and the job is chosen
   * explicitly, so a second crew open at once would mean a second job in play
   * and a save that could quietly use the wrong one.
   */
  /* --- Correcting hours ---------------------------------------------------
   *
   * A supervisor who hits 5 meaning 8 knows it immediately, and until now the
   * only way to fix it was to ask the office. The API already allowed this --
   * correct_entry is Supervisor + enforce_own_crew, audited, and refuses
   * anything exported -- so the whole gap was that this screen never offered
   * it.
   *
   * NO APPROVAL STEP, deliberately. Under Phase 1 implicit approval the
   * supervisor's original entry IS the approval, so requiring a PM to sign off
   * on the correction would make a typo harder to fix than it was to make. The
   * audit trail answers who changed what.
   *
   * The grid shows a day TOTAL and a correction targets a LINE, so opening a
   * cell fetches the lines behind it -- usually one, sometimes regular plus
   * travel.
   */
  const [openCell, setOpenCell] = useState<string | null>(null)
  const [cellLines, setCellLines] = useState<DayLine[]>([])
  const [cellLoading, setCellLoading] = useState(false)
  const [cellError, setCellError] = useState<string | null>(null)
  const [hoursDraft, setHoursDraft] = useState<Record<string, string>>({})
  const [savingLine, setSavingLine] = useState<string | null>(null)
  /* WHY THE CHANGE WAS MADE. Required of supervisors (Tim Brickell, 9 Oct
     2026: "even if it's just 'typo'"). This screen used to fill in a stock
     sentence instead, which said where the change came from but never why --
     and why is the part management will ask. */
  const [cellNote, setCellNote] = useState('')

  const cellId = (key: string, iso: string) => `${key}|${iso}`

  async function openDay(key: string, iso: string) {
    const id = cellId(key, iso)
    if (openCell === id) {
      setOpenCell(null)
      return
    }
    setOpenCell(id)
    setCellLines([])
    setCellError(null)
    setCellNote('')
    setCellLoading(true)
    try {
      const lines = await api.dayLines(key, iso)
      setCellLines(lines)
      setHoursDraft(
        Object.fromEntries(
          lines.map((l) => [l.time_entry_id, String(l.hours_worked ?? '')]),
        ),
      )
    } catch (err) {
      setCellError(err instanceof ApiError ? err.message : 'Could not load that day')
    } finally {
      setCellLoading(false)
    }
  }

  async function saveLine(line: DayLine) {
    const next = Number(hoursDraft[line.time_entry_id])
    if (!Number.isFinite(next) || next < 0 || next > 24) {
      setCellError('Hours must be between 0 and 24.')
      return
    }
    if (next === Number(line.hours_worked)) {
      setOpenCell(null)
      return
    }
    if (isSupervisor && !cellNote.trim()) {
      setCellError('Add a note saying why - even just "typo".')
      return
    }
    setSavingLine(line.time_entry_id)
    setCellError(null)
    try {
      await api.correctEntry(line.submission_id, line.time_entry_id, {
        hours_worked: next,
        reason: cellNote.trim() || null,
      })
      setOpenCell(null)
      // Reload rather than patching in place: the day total, the week total
      // and the crew total all move, and recomputing them here would be three
      // more chances to disagree with the server.
      await load()
    } catch (err) {
      setCellError(err instanceof ApiError ? err.message : 'Could not save that change')
    } finally {
      setSavingLine(null)
    }
  }

  const [editingCrew, setEditingCrew] = useState<number | null>(null)
  // "employeeKey|iso-date" for each box ticked but not yet saved.
  const [pending, setPending] = useState<Set<string>>(new Set())
  /* Boxes that WERE paid and have been unticked. Removal reuses the office's
     per diem removal: all or nothing, own crew only, refused once a line has
     reached Sage or Sage has paid the period. A note is required of a
     supervisor, as for any other change to recorded time. */
  const [removing, setRemoving] = useState<Set<string>>(new Set())
  const [pdNote, setPdNote] = useState('')
  const [pdJobNo, setPdJobNo] = useState('')
  const [jobs, setJobs] = useState<JobSummary[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<PerDiemConflict[]>([])

  useEffect(() => {
    void (async () => {
      try {
        const p = await api.payPeriods()
        setPeriods(p)
        // Open on the most recent week that actually HAS time. Defaulting to
        // the last completed week sounds tidier but lands on an empty grid
        // whenever entry runs a week behind, which reads as a broken screen.
        const withTime = p.find((x) => x.has_time_entered)
        setWeekEnding((withTime ?? p[0])?.week_ending ?? '')
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load pay periods')
      }
    })()
  }, [])

  const load = useCallback(async () => {
    if (!weekEnding) return
    setLoading(true)
    setError(null)
    try {
      setCrews(await api.crewWeek(weekEnding))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the week')
    } finally {
      setLoading(false)
    }
  }, [weekEnding])

  useEffect(() => {
    void load()
  }, [load])

  const period = periods.find((p) => p.week_ending === weekEnding)

  // Sage has paid this week, so nothing more can be added to it — the same
  // reason a posted crew day refuses corrections. The server refuses too;
  // this only keeps the button from being offered in the first place.
  //
  // The supervisor cutoff closes it to supervisors earlier (Pay setup →
  // Supervisor cutoff). The office is never held to that.
  const closedToMe = isSupervisor && period?.closed_to_supervisors === true
  const periodClosed = period?.paid_in_sage === true || closedToMe

  function startEditing(supervisorId: number) {
    setEditingCrew(supervisorId)
    setPending(new Set())
    setRemoving(new Set())
    setPdNote('')
    setPdJobNo('')
    setSaveError(null)
    setConflicts([])
    if (jobs.length === 0) {
      void api.jobs().then(setJobs).catch(() => {
        setSaveError('Could not load the job list.')
      })
    }
  }

  function stopEditing() {
    setEditingCrew(null)
    setPending(new Set())
    setRemoving(new Set())
    setPdNote('')
    setPdJobNo('')
    setSaveError(null)
    setConflicts([])
  }

  function toggleRemove(key: string) {
    setRemoving((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function toggleBox(key: string) {
    setPending((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function savePerDiem(crew: CrewWeek) {
    if (removing.size > 0 && isSupervisor && !pdNote.trim()) {
      setSaveError('Add a note saying why the per diem is coming off.')
      return
    }
    setSaving(true)
    setSaveError(null)
    setConflicts([])

    // REMOVALS FIRST, and on their own. The server applies them all or none,
    // so if this fails nothing has changed and the additions are not tried.
    if (removing.size > 0) {
      const ids = crew.members.flatMap((m) =>
        DAYS.flatMap((_, i) =>
          removing.has(`${m.sage_employee_key}|${dayIso(crew.week_ending, i)}`)
            ? (m.per_diem_entry_ids_by_day[i] ?? [])
            : [],
        ),
      )
      try {
        await api.removePerDiemDays(ids, pdNote.trim() || undefined)
      } catch (err) {
        setSaveError(
          'Nothing was changed. ' +
            (err instanceof ApiError ? err.message : 'Could not remove that per diem.'),
        )
        setSaving(false)
        await load()
        return
      }
      if (pending.size === 0) {
        stopEditing()
        await load()
        setSaving(false)
        return
      }
    }

    try {
      const entries = [...pending].map((k) => {
        const [sage_employee_key, date_worked] = k.split('|')
        return { sage_employee_key, date_worked }
      })
      // One id per distinct date, so a retry cannot double-record.
      const submissions: Record<string, string> = {}
      for (const e of entries) submissions[e.date_worked] ??= crypto.randomUUID()

      await api.recordPerDiemDays({
        job_no: pdJobNo,
        entries,
        submissions,
        client_captured_utc: new Date().toISOString(),
      })
      stopEditing()
      await load()
    } catch (err) {
      if (err instanceof ApiError) {
        const detail = err.detail as
          | { message?: string; conflicts?: PerDiemConflict[] }
          | undefined
        if (detail?.conflicts) {
          setConflicts(detail.conflicts)
          setSaveError(detail.message ?? 'Some of those days are already recorded.')
        } else {
          setSaveError(err.message)
        }
        // The removals, if any, already went through; say so rather than
        // leave it looking as if the whole save failed.
        if (removing.size > 0) {
          setSaveError((m) => `Per diem removed, but the days being added were not saved. ${m ?? ''}`)
          setRemoving(new Set())
        }
        // Someone else may have recorded them since this screen loaded.
        await load()
      } else {
        setSaveError('Could not record per diem.')
      }
    } finally {
      setSaving(false)
    }
  }

  /** Jobs offered for the days being added: this crew's week first, then the
   *  rest. Never defaulted — per diem is costed to a job, and a guess would be
   *  right most of the time and wrong silently. */
  function jobOptions(crew: CrewWeek) {
    const mine = new Set(crew.jobs.map((j) => j.job_no))
    return [
      ...crew.jobs.map((j) => ({ job_no: j.job_no, label: j.job_description ?? '' })),
      ...jobs
        .filter((j) => !mine.has(j.job_no))
        .map((j) => ({ job_no: j.job_no, label: j.job_description ?? '' })),
    ]
  }

  const grand = useMemo(
    () => ({
      hours: crews.reduce((s, c) => s + Number(c.total_hours), 0),
      perDiem: crews.reduce((s, c) => s + Number(c.total_per_diem), 0),
      people: new Set(crews.flatMap((c) => c.members.map((m) => m.sage_employee_key))).size,
    }),
    [crews],
  )

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h2 className="title" style={{ marginTop: 0 }}>
          {isSupervisor ? 'My crew by week' : 'Crews by pay period'}
        </h2>
        <div className="form-field" style={{ maxWidth: 340, marginBottom: 0 }}>
          <label htmlFor="week">Week ending</label>
          <select
            id="week"
            value={weekEnding}
            onChange={(e) => setWeekEnding(e.target.value)}
          >
            {periods.map((p) => (
              <option key={p.week_ending} value={p.week_ending}>
                {dayLabel(p.week_starting)} – {dayLabel(p.week_ending, true)}
                {p.has_time_entered ? '' : ' · no time entered'}
                {p.paid_in_sage ? ' · paid' : ''}
              </option>
            ))}
          </select>
        </div>
        <p className="muted" style={{ marginTop: '0.6rem' }}>
          Pay periods end on a Saturday. {period?.paid_in_sage
            ? `Sage paid this period on ${period.check_date}.`
            : 'Sage has not paid this period yet.'}
          {period && !period.paid_in_sage && period.closes_at && (
            <> {cutoffLine(period)}</>
          )}
        </p>
      </div>

      {/* Stays under the nav as you scroll. Without it you are several
          screens into a grid of hours with no idea which week they are. */}
      {period && (
        <div className="period-bar">
          <span className="period-week">
            {dayLabel(period.week_starting)} – {dayLabel(period.week_ending, true)}
          </span>
          {!period.has_time_entered ? (
            <span className="period-pill is-empty">No time entered</span>
          ) : period.paid_in_sage ? (
            <span className="period-pill is-paid">Paid {period.check_date}</span>
          ) : period.closed_to_supervisors ? (
            <span className="period-pill is-paid">Closed to supervisors</span>
          ) : (
            <span className="period-pill is-open">Open</span>
          )}
          {crews.length > 0 && (
            <span className="period-figures">
              {crews.length} crew{crews.length === 1 ? '' : 's'} ·{' '}
              {grand.people} people · {grand.hours.toFixed(2)} h
              {grand.perDiem > 0 && ` · $${grand.perDiem.toFixed(2)} per diem`}
            </span>
          )}
        </div>
      )}

      {loading && <div className="card"><p className="muted">Loading…</p></div>}

      {!loading && crews.length === 0 && (
        <div className="card">
          <p className="muted">
            No time recorded for this week
            {isSupervisor ? ' by you' : ''}.
          </p>
        </div>
      )}

      {crews.map((c) => (
        <div className="card" key={c.supervisor_employee_id}>
          <div className="crew-heading">
            <h2 className="title" style={{ margin: 0 }}>
              {c.supervisor_name}
            </h2>
            <span className="muted">
              {Number(c.total_hours).toFixed(2)} h
              {Number(c.total_per_diem) > 0 &&
                ` · $${Number(c.total_per_diem).toFixed(2)} per diem`}
            </span>
            {/* Per diem is paid on days nobody works — the weekend a crew
                stays over. Recording it here rather than as a crew day is the
                point: there are no hours to enter. */}
            {!periodClosed && (
              <button
                className="btn btn-secondary"
                style={{ marginLeft: 'auto' }}
                onClick={() =>
                  editingCrew === c.supervisor_employee_id
                    ? stopEditing()
                    : startEditing(c.supervisor_employee_id)
                }
              >
                {editingCrew === c.supervisor_employee_id ? 'Cancel' : 'Per diem'}
              </button>
            )}
          </div>
          <p className="muted" style={{ fontSize: '0.8125rem' }}>
            {c.jobs.map((j) => `${j.job_no} ${j.job_description ?? ''}`.trim()).join(' · ')}
          </p>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th></th>
                  {DAYS.map((d) => (
                    <th key={d} className="num">{d}</th>
                  ))}
                  <th className="num">Hours</th>
                  <th className="num">Per diem</th>
                </tr>
              </thead>
              <tbody>
                {c.members.map((m) => (
                  <Fragment key={m.sage_employee_key}>
                  <tr>
                    <td>
                      {m.display_name}
                      {/* More than one job means they moved mid-week — the
                          thing a per-job view cannot show at all. */}
                      {m.job_count > 1 && (
                        <span className="muted" style={{ fontSize: '0.75rem' }}>
                          {' '}· {m.job_count} jobs
                        </span>
                      )}
                    </td>
                    <td>
                      {m.crew_role && (
                        <span className={`role-badge ${ROLE_CLASS[m.crew_role] ?? ''}`}>
                          {m.crew_role}
                        </span>
                      )}
                    </td>
                    {m.days.map((h, i) => {
                      const iso = dayIso(weekEnding, i)
                      const id = cellId(m.sage_employee_key, iso)
                      // Only a day with hours has lines to correct. An empty
                      // cell is a per diem question, and that has its own row.
                      return (
                        <td key={i} className="num">
                          {Number(h) > 0 ? (
                            <button
                              className="btn-plain"
                              style={{
                                font: 'inherit',
                                textDecoration:
                                  openCell === id ? 'underline' : 'none',
                              }}
                              onClick={() => void openDay(m.sage_employee_key, iso)}
                              aria-expanded={openCell === id}
                              title="Correct these hours"
                            >
                              {Number(h).toFixed(2)}
                            </button>
                          ) : (
                            <span className="muted">–</span>
                          )}
                        </td>
                      )
                    })}
                    <td className="num"><strong>{Number(m.total_hours).toFixed(2)}</strong></td>
                    <td className="num">
                      {m.per_diem_days > 0
                        ? `${m.per_diem_days} d · $${Number(m.per_diem_amount).toFixed(2)}`
                        : <span className="muted">–</span>}
                    </td>
                  </tr>

                  {/* The lines behind one cell, opened by tapping it. */}
                  {openCell?.startsWith(`${m.sage_employee_key}|`) && (
                    <tr>
                      <td colSpan={11} style={{ background: 'var(--c-bg-alt, transparent)' }}>
                        {cellLoading && <span className="muted">Loading…</span>}
                        {cellError && (
                          <div className="alert alert-error" style={{ margin: 0 }}>
                            {cellError}
                          </div>
                        )}
                        {!cellLoading && !cellError && cellLines.length === 0 && (
                          <span className="muted">No hour lines on that day.</span>
                        )}
                        {cellLines.some((l) => l.is_editable) && (
                          <input
                            value={cellNote}
                            onChange={(e) => setCellNote(e.target.value)}
                            placeholder={
                              isSupervisor
                                ? 'Why the change? (required — e.g. typo)'
                                : 'Why the change? (optional)'
                            }
                            maxLength={300}
                            aria-label="Reason for the change"
                            style={{ marginBottom: '0.5rem', maxWidth: 420 }}
                          />
                        )}
                        {cellLines.map((l) => (
                          <div
                            key={l.time_entry_id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              marginBottom: '0.4rem',
                              flexWrap: 'wrap',
                            }}
                          >
                            <strong>{l.work_type_label}</strong>
                            <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                              {l.job_no}
                            </span>
                            {l.is_editable ? (
                              <>
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="0.25"
                                  min="0"
                                  max="24"
                                  style={{ width: '5.5rem' }}
                                  value={hoursDraft[l.time_entry_id] ?? ''}
                                  onChange={(e) =>
                                    setHoursDraft((d) => ({
                                      ...d,
                                      [l.time_entry_id]: e.target.value,
                                    }))
                                  }
                                  aria-label={`Hours for ${l.work_type_label}`}
                                />
                                <button
                                  className="btn"
                                  disabled={savingLine === l.time_entry_id}
                                  onClick={() => void saveLine(l)}
                                >
                                  {savingLine === l.time_entry_id ? 'Saving…' : 'Save'}
                                </button>
                              </>
                            ) : (
                              <>
                                <span>{Number(l.hours_worked ?? 0).toFixed(2)} h</span>
                                {/* Locked, and the reason matters: one means
                                    fix it in Sage, the other means payroll has
                                    already run. */}
                                <span className="badge badge-warn">
                                  {l.locked_reason ?? 'Locked'}
                                </span>
                              </>
                            )}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}

                  {/* The tick row. Days already paid render ticked and
                      disabled, so the duplicate rule is visible before it is
                      hit rather than as an error afterwards. */}
                  {editingCrew === c.supervisor_employee_id && (
                    <tr>
                      <td className="muted" style={{ fontSize: '0.75rem' }}>
                        Per diem
                      </td>
                      <td />
                      {DAYS.map((_, i) => {
                        const iso = dayIso(c.week_ending, i)
                        const paidAmount = m.per_diem_by_day[i]
                        const alreadyPaid = paidAmount !== null && paidAmount !== undefined
                        const boxKey = `${m.sage_employee_key}|${iso}`
                        const unticked = removing.has(boxKey)
                        return (
                          <td key={i} className="num">
                            <input
                              type="checkbox"
                              checked={alreadyPaid ? !unticked : pending.has(boxKey)}
                              onChange={() =>
                                alreadyPaid ? toggleRemove(boxKey) : toggleBox(boxKey)
                              }
                              aria-label={`Per diem for ${m.display_name} on ${DAYS[i]}`}
                              title={
                                alreadyPaid
                                  ? (unticked ? 'Will be removed: ' : 'Recorded: ') +
                                    `$${Number(paidAmount).toFixed(2)}` +
                                    ` on job ${m.per_diem_job_by_day[i] ?? '—'}`
                                  : undefined
                              }
                            />
                          </td>
                        )
                      })}
                      <td />
                      <td />
                    </tr>
                  )}
                  </Fragment>
                ))}
                <tr>
                  <td colSpan={2}><strong>Crew total</strong></td>
                  {DAYS.map((_, i) => {
                    const day = c.members.reduce((s, m) => s + Number(m.days[i] ?? 0), 0)
                    return (
                      <td key={i} className="num">
                        <strong>{day > 0 ? day.toFixed(2) : '–'}</strong>
                      </td>
                    )
                  })}
                  <td className="num"><strong>{Number(c.total_hours).toFixed(2)}</strong></td>
                  <td className="num"><strong>${Number(c.total_per_diem).toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          {editingCrew === c.supervisor_employee_id && (
            <div className="stack" style={{ marginTop: '1rem' }}>
              {saveError && <div className="alert alert-error">{saveError}</div>}

              {conflicts.length > 0 && (
                <div className="alert alert-warn">
                  Nothing was saved. Already recorded:{' '}
                  {conflicts
                    .map(
                      (x) =>
                        `${x.display_name ?? x.sage_employee_key} on ` +
                        `${dayLabel(x.date_worked)} (job ${x.job_no})`,
                    )
                    .join('; ')}
                  .
                </div>
              )}

              {removing.size > 0 && (
                <div className="form-field" style={{ maxWidth: 460, marginBottom: 0 }}>
                  <label htmlFor={`pdnote-${c.supervisor_employee_id}`}>
                    Why is per diem coming off {removing.size} day
                    {removing.size === 1 ? '' : 's'}?
                    {isSupervisor ? '' : ' (optional)'}
                  </label>
                  <input
                    id={`pdnote-${c.supervisor_employee_id}`}
                    value={pdNote}
                    onChange={(e) => setPdNote(e.target.value)}
                    placeholder="e.g. went home Thursday night"
                    maxLength={300}
                  />
                </div>
              )}

              {pending.size > 0 && (
              <div className="form-field" style={{ maxWidth: 460, marginBottom: 0 }}>
                <label htmlFor={`pdjob-${c.supervisor_employee_id}`}>
                  Job for the days you are adding
                </label>
                <select
                  id={`pdjob-${c.supervisor_employee_id}`}
                  value={pdJobNo}
                  onChange={(e) => setPdJobNo(e.target.value)}
                >
                  <option value="">Pick a job…</option>
                  {jobOptions(c).map((j) => (
                    <option key={j.job_no} value={j.job_no}>
                      {j.job_no} {j.label}
                    </option>
                  ))}
                </select>
              </div>
              )}
              <p className="muted" style={{ margin: 0, fontSize: '0.8125rem' }}>
                Tick a box to add per diem; untick a recorded one to take it
                off. Per diem is costed to a job, so it is chosen rather than
                assumed — including for days nobody worked. The rate comes from
                the job's location and each person's tier.
              </p>

              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  className="btn"
                  disabled={
                    (pending.size === 0 && removing.size === 0) ||
                    (pending.size > 0 && !pdJobNo) ||
                    saving
                  }
                  onClick={() => void savePerDiem(c)}
                >
                  {saving
                    ? 'Saving…'
                    : [
                        pending.size > 0 &&
                          `add ${pending.size} day${pending.size === 1 ? '' : 's'}`,
                        removing.size > 0 &&
                          `remove ${removing.size} day${removing.size === 1 ? '' : 's'}`,
                      ]
                        .filter(Boolean)
                        .join(', ')
                        .replace(/^./, (ch) => `Save: ${ch}`) || 'Save'}
                </button>
                <button className="btn btn-secondary" onClick={stopEditing} disabled={saving}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
